import { GeofenceGroup, GeofenceType } from '@eagle/core-data-types';
import { styled, TableCell, Typography } from '@ethings/ui-toolkit';
import { Tooltip } from '@mui/material';
import { FC, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref } from 'react-router-dom';
import { useCustomRoutes } from '../../hooks';
import { ResultsTableRow } from '../search-results-table';
import { SharedIcon } from '../shared-icon';
import { BoldMatchedText } from '../text';

const StyledTableCell = styled(TableCell)();

const TableDisplay: FC<{ display: string; deleted: boolean }> = ({ display, deleted = false }) => {
  const { t } = useTranslation(['common']);
  return (
    <StyledTableCell component="div">
      <Tooltip arrow followCursor disableHoverListener={!deleted} title={<>{t('common:common.hint.deleted')}</>}>
        <Typography data-testid="name"><BoldMatchedText text={display} /></Typography>
      </Tooltip>
    </StyledTableCell>
  );
};

export const GeofenceTableRow: FC<{ geofenceGroup: GeofenceGroup; 'data-testid'?: string }> = ({ geofenceGroup, ...props }) => {
  const { geofence: customRoutesGeofence, group: customRoutesGroup } = useCustomRoutes();
  const href = useHref(`/${customRoutesGeofence}/${customRoutesGroup}`);
  const { _id, display, deleted } = geofenceGroup;

  const renderContent = (): JSX.Element => <TableDisplay display={display} deleted={deleted !== null} />;

  return (
    <ResultsTableRow
      content={renderContent()}
      data-testid={props['data-testid']}
      href={`${href}/${_id}`}
      deleted={deleted !== null}
    />
  );
};

export const GeofenceTypesTableRow: FC<{ geofenceType: GeofenceType; 'data-testid'?: string }> = ({ geofenceType, ...props }) => {
  const { geofence: customRoutesGeofence } = useCustomRoutes();
  const href = useHref(`/${customRoutesGeofence}/type`);
  const { _id, display, shared, deleted, accountBinding } = geofenceType;

  const renderContent = (): JSX.Element => <>
    <TableDisplay display={display} deleted={deleted !== null} />
    <StyledTableCell component="div" sx={{ width: 100, textAlign: 'center' }} >
      {shared && <SharedIcon accountBinding={accountBinding} shared={shared} deleted={Boolean(deleted)} />}
    </StyledTableCell>
  </>;

  return (
    <ResultsTableRow
      data-testid={props['data-testid']}
      content={renderContent()}
      href={`${href}/${_id}`}
      deleted={deleted !== null}
    />
  );
};
